<template>
  <SectionCard :id="loanAnchorId(loan.publicLoanNumber)" :title="cardTitle">
    <div class="card-columns">
      <!-- Principal -->
      <DataSection title="Lånebelopp">
        <DataField label="Nuvarande kapitalskuld">{{ formatAmountWithCurrency(loan.principal.current) }}</DataField>
        <DataField label="Ursprunglig kapitalskuld">{{ formatAmountWithCurrency(loan.principal.original) }}</DataField>
        <DataField label="Lånets utbetalningsdag">{{ formatDateIfValid(loan.lifeCycle.startDate) }}</DataField>
        <DataField label="Lånets förfallodag">{{ formatDateIfValid(loan.lifeCycle.maturityDate) ?? '-' }}</DataField>
      </DataSection>

      <!-- Pricing -->
      <DataSection title="Ränta">
        <DataField label="Aktuell ränta">
          {{ formatPercentage(loan.pricing.interestRate) }}
        </DataField>
        <DataField label="Effektiv ränta">
          {{ formatPercentage(loan.pricing.effectiveInterestRate) }}
        </DataField>
        <DataField v-if="loan.loanTerms.discounts.length" label="Rabatt">
          {{ formatDiscounts(loan.loanTerms.discounts) }}
        </DataField>
      </DataSection>

      <!-- Interest Term -->
      <DataSection title="Räntebindning">
        <DataField label="Aktuell räntebindningsperiod">
          {{ t(`INTEREST_TERM.${loan.interestTerm.interestTerm}`) }}
        </DataField>
        <DataField label="Nästa ränteändringsdag">
          {{ formatDateIfValid(loan.interestTerm.nextInterestTermsChangeDate) ?? '-' }}
        </DataField>
        <DataField v-if="loan.interestTerm.nextInterestTerm" label="Kommande räntebindningsperiod">
          {{ t(`INTEREST_TERM.${loan.interestTerm.nextInterestTerm}`) }}
        </DataField>
      </DataSection>

      <!-- Loan Terms -->
      <DataSection title="Villkorsperiod">
        <DataField label="Nästa villkorsändringsdag">
          {{ formatDateIfValid(loan.loanTerms.nextLoanTermsChangeDate) ?? '-' }}
        </DataField>
        <DataField label="Månadsamortering">
          <span :class="{ 'is-superseded': pendingAmortization !== null }">
            {{ formatAmount(loan.loanTerms.amortizationPerPeriod) }} kr/mån
          </span>
          <span v-if="pendingAmortization !== null" class="text-subtle">
            {{ pendingAmortizationNote(pendingAmortization) }}
          </span>
        </DataField>
        <DataField label="Produktionspris">
          {{ loan.pricing.borgoProductionPrice !== null ? loan.pricing.borgoProductionPrice : '-' }}
        </DataField>
      </DataSection>

      <!-- Information / Lifecycle -->
      <DataSection title="Lånenummer">
        <DataField label="Lånenummer">{{ loan.publicLoanNumber }}</DataField>
        <DataField v-if="loan.externalLegacyLoan" label="Tidigare lånenummer">{{ loan.externalLegacyLoan }}</DataField>
        <DataField label="Lånets ID">{{ loan.id }}</DataField>
      </DataSection>
    </div>
  </SectionCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { SectionCard } from '@staff/ui'
import { formatAmount, formatAmountWithCurrency, formatDateIfValid, formatPercentage } from '@staff/shared'

import type { Debt } from '../debt.types'
import { loanAnchorId } from '../debt.utils'
import DataField from '@/components/DataField.vue'
import DataSection from '@/components/DataSection.vue'
import { pendingAmortizationNote, pendingAmortizationPerPeriod } from '@/utils/amortizationDisplay'

type Loan = Debt['loans'][number]

const { t } = useI18n()

const props = defineProps<{
  loan: Loan
  loanIndex: number
  loanCount: number
}>()

const pendingAmortization = computed(() => pendingAmortizationPerPeriod(props.loan))

const cardTitle = computed(() => {
  // "Bolån" names the whole debt, so a mortgage loan part is titled by its loan number instead.
  const typeLabel = props.loan.type === 'mortgage' ? 'Lånenummer' : t(`LOAN_TYPE.${props.loan.type}`)
  return `${typeLabel} ${props.loan.publicLoanNumber} (${props.loanIndex + 1}/${props.loanCount})`
})

const formatDiscounts = (discounts: Loan['loanTerms']['discounts']) => {
  return discounts.map((d) => `${d.bps} bps (${d.reason})`).join(', ')
}
</script>

<style scoped lang="scss">
// A value that a pending change has replaced.
.is-superseded {
  text-decoration-line: line-through;
}

.card-columns {
  display: flex;
  max-width: 100%;
  flex-direction: column;
  gap: var(--space-20);
}

// Cards read better in a narrow column on wide screens; full width below that.
@media (min-width: 80rem) {
  .card-columns {
    max-width: 50%;
  }
}
</style>
